import DialogUtils from "../DialogUtils.js";

export default class BaseDialogHtml {
    #dialog;
    #callbacks = {
        onOk: ()=>{},
        onCancel: ()=>{},
        onSecondary: ()=>{}
    }

    show({title, bodyHtml, cssClass, btnOk, btnCancel, autofocusEl, btnSecondary}) {
        const dialog = this.#getDialogEl();
        dialog.classList.add(cssClass);
        dialog.innerHTML = this.#template(title, bodyHtml, btnOk, btnCancel, btnSecondary);
        this.#setEventListeners();
        this.#dialog.showModal();
        if (autofocusEl != null) {
            const elementToFocus = this.#dialog.querySelector(autofocusEl);
            if (!elementToFocus)
                throw new Error(`Element[${autofocusEl}] which is specified as autofocus is not present in the dialog`);
            elementToFocus.focus();
        }
    }

    onOk(callback) {
        this.#callbacks.onOk = callback;
    }

    onCancel(callback) {
        this.#callbacks.onCancel = callback;
    }

    onSecondary(callback) {
        this.#callbacks.onSecondary = callback;
    }

    close(){
        this.#remove();
    }

    /** @return {boolean} */
    isAlreadyShown() {
        return !!this.#dialog;
    }
    /**
     * @param {string} selector
     * @returns {HTMLElement|null}
     */
    getElement(selector) {
        return this.#dialog.querySelector(selector);
    }
    #remove() {
        if (!this.#dialog)
            return;
        this.#dialog.remove();
        this.#dialog = null;
    }

    #setEventListeners() {
        DialogUtils.setDefaultDialogListeners(this.#dialog);
        const okBtn = this.#getOkButtonElement();
        if (okBtn)
            okBtn.addEventListener("click", this.#onOk.bind(this));
        const secondaryBtn = this.#getSecondaryButtonElement();
        if (secondaryBtn)
            secondaryBtn.addEventListener("click", this.#onSecondary.bind(this));
        this.#dialog.addEventListener("cancel", this.#onCancel.bind(this));
    }

    async #onOk() {
        this.#setAsyncTaskInProgress(true);
        await this.#callbacks.onOk();
        this.#remove();
    }

    #onCancel() {
        this.#callbacks.onCancel();
        this.#remove();
    }

    #onSecondary() {
        this.#callbacks.onSecondary();
    }

    #setAsyncTaskInProgress(inProgress) {
        this.#dialog.querySelector('.progress').style.display = inProgress ? "block" : "none";
        this.#dialog.querySelectorAll('button').forEach(btn => btn.setAttribute('disabled', inProgress));
    }

    #getOkButtonElement() {
        return this.#dialog.querySelector('.btn-ok');
    }

    /** @returns {HTMLElement} */
    #getSecondaryButtonElement() {
        return this.#dialog.querySelector('.btn-secondary');
    }

    #getDialogEl() {
        if (!this.#dialog) {
            const dialog = document.body.insertAdjacentElement("beforeend", document.createElement('dialog'));
            dialog.classList.add('base-dialog');
            this.#dialog = dialog
        }
        return this.#dialog;
    }

    #template(title, bodyHtml, btnOk, btnCancel, btnSecondary) {
        const okBtn = btnOk? `<button class="button button--contained btn-ok" autofocus>${btnOk}</button>` : "";
        const cancelBtn = btnCancel? `<button class="button button--text btn-cancel">${btnCancel}</button>` : "";
        const secondaryBtn = btnSecondary? `<button class="button button--outlined btn-secondary">${btnSecondary}</button>` : "";
        return `
          <div class="progress"><div class="indeterminate"></div></div>
          <header class="dialog__header">${title}</header>
          ${bodyHtml}
          <footer class="dialog__footer dialog__footer--main-buttons-only">
              ${cancelBtn}
              ${secondaryBtn}
              ${okBtn}
          </footer>`;
    }
}